import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Pencil, Globe, Lock, Copy, Trash2, ExternalLink } from "lucide-react";
import { OfferDialog } from "./OfferDialog";
import { Badge, OFFER_STATUS, KIND_BADGE, fmtDate } from "./recruitLabels";

const FILTERS = [["all", "Toutes"], ["published", "Publiées"], ["draft", "Brouillons"], ["closed", "Fermées"]];

export const OffersTab = ({ catalog, onChanged, onShowCandidates }) => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [dialog, setDialog] = useState({ open: false, offer: null });
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/recrutement/offers", { params: { status } }); setItems(data.items); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const act = async (o, action) => {
    setBusy(o.id + action);
    try {
      if (action === "delete") {
        if (!window.confirm(`Supprimer l'offre ${o.reference} ?`)) return;
        await api.delete(`/admin/recrutement/offers/${o.id}`);
        toast.success("Offre supprimée");
      } else {
        const { data } = await api.post(`/admin/recrutement/offers/${o.id}/${action}`);
        toast.success({ publish: `Offre publiée — visible sur /carrieres/${data.id}`, close: "Offre fermée", duplicate: `Copie créée : ${data.reference}` }[action]);
      }
      await Promise.all([load(), onChanged?.()]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="bg-white border border-[#DEE2E6]" data-testid="offers-tab">
      <header className="p-4 border-b border-[#DEE2E6] flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setStatus(k)} data-testid={`offers-filter-${k}`}
                    className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5 border transition ${status === k ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-[#DEE2E6] hover:border-[#002FA7]"}`}>{l}</button>
          ))}
        </div>
        <div className="flex-1" />
        <a href="/carrieres" target="_blank" rel="noreferrer" className="text-[11px] font-semibold inline-flex items-center gap-1 hover:text-[#002FA7]" data-testid="offers-open-careers"><ExternalLink className="h-3.5 w-3.5" />Page Carrières</a>
        <Button size="sm" onClick={() => setDialog({ open: true, offer: null })} className="h-8 bg-[#002FA7] text-white" data-testid="offers-new"><Plus className="h-3.5 w-3.5 mr-1" />Nouvelle offre</Button>
      </header>
      {items.length === 0 ? <div className="p-10 text-center text-sm text-[#868E96]" data-testid="offers-empty">Aucune offre — créez votre première offre d'emploi ou mandat Business Partner.</div> : (
        <div className="divide-y divide-[#DEE2E6]">
          {items.map((o) => (
            <div key={o.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`offer-row-${o.id}`}>
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-mono text-[#868E96]">{o.reference}</span>
                  <Badge cfg={KIND_BADGE[o.kind]} />
                  <Badge cfg={OFFER_STATUS[o.status]} testid={`offer-status-${o.id}`} />
                </div>
                <div className="font-display font-bold text-base mt-1">{o.title}</div>
                <div className="text-[11px] text-[#495057]">{catalog?.contracts?.[o.contract_type]} {o.location && `· ${o.location}`} {o.salary_label && `· ${o.salary_label}`} {o.deadline && `· jusqu'au ${fmtDate(o.deadline)}`}</div>
              </div>
              <button type="button" onClick={() => onShowCandidates(o.id)} className="text-center px-3 hover:bg-[#F8F9FA]" data-testid={`offer-candidates-${o.id}`}>
                <div className="font-display font-black text-2xl">{o.candidates_count || 0}</div>
                <div className="text-[9px] uppercase tracking-wider text-[#868E96]">candidat(s)</div>
              </button>
              <div className="flex flex-wrap gap-1">
                <Button size="sm" variant="outline" onClick={() => setDialog({ open: true, offer: o })} className="h-8" data-testid={`offer-edit-${o.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                {o.status !== "published" && <Button size="sm" onClick={() => act(o, "publish")} disabled={!!busy} className="h-8 bg-[#059669] text-white" data-testid={`offer-publish-${o.id}`}><Globe className="h-3.5 w-3.5 mr-1" />Publier</Button>}
                {o.status === "published" && <Button size="sm" variant="outline" onClick={() => act(o, "close")} disabled={!!busy} className="h-8" data-testid={`offer-close-${o.id}`}><Lock className="h-3.5 w-3.5 mr-1" />Fermer</Button>}
                <Button size="sm" variant="outline" onClick={() => act(o, "duplicate")} disabled={!!busy} className="h-8" data-testid={`offer-duplicate-${o.id}`}><Copy className="h-3.5 w-3.5" /></Button>
                {!o.candidates_count && <Button size="sm" variant="outline" onClick={() => act(o, "delete")} disabled={!!busy} className="h-8 text-[#E60000]" data-testid={`offer-delete-${o.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}
      <OfferDialog open={dialog.open} onOpenChange={(v) => setDialog((d) => ({ ...d, open: v }))} offer={dialog.offer} catalog={catalog} onSaved={() => { load(); onChanged?.(); }} />
    </div>
  );
};
